import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { BASE_URL, PAYPAL_CLIENT_ID } from "../../config/RequestMethod";
import { PayPalButtons, PayPalScriptProvider } from "@paypal/react-paypal-js";
import {
  Box,
  Container,
  Flex,
  Heading,
  Text,
  Badge,
  Button,
  VStack,
  HStack,
  Progress,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Image,
  Divider,
  useToast,
  Grid,
  GridItem,
  Icon,
  Card,
  CardBody,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  CloseButton,
} from "@chakra-ui/react";
import {
  FaCheckCircle,
  FaLock,
  FaUser,
  FaBuilding,
  FaMoneyBillWave,
  FaInfoCircle,
  FaReceipt,
} from "react-icons/fa";

// --- HELPER: Load Razorpay Script Dynamically ---
const loadRazorpayScript = (src) => {
  return new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = src;
    script.onload = () => {
      resolve(true);
    };
    script.onerror = () => {
      resolve(false);
    };
    document.body.appendChild(script);
  });
};

const ClientPaymentPortal = () => {
  const [searchParams] = useSearchParams();
  const proposalId = searchParams.get("proposalId");
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeEmiId, setActiveEmiId] = useState(null);

  // New state to handle inline errors specific to an EMI card
  const [paymentStatus, setPaymentStatus] = useState({
    emiId: null,
    type: null, // 'error' | 'success' | 'warning'
    message: null,
  });

  const toast = useToast();

  useEffect(() => {
    const fetchProposal = async () => {
      try {
        const res = await axios.get(`${BASE_URL}client-proposal/${proposalId}`);
        setProposal(res.data.Data);
      } catch (err) {
        toast({
          title: "Error",
          description: "Invalid Link or Proposal not found.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };
    fetchProposal();
  }, [proposalId, toast]);

  // Helper to clear errors when switching tabs or starting new payment
  const clearError = () => {
    setPaymentStatus({ emiId: null, type: null, message: null });
  };

  const createOrder = async (data, actions) => {
    clearError();
    try {
      const res = await axios.post(`${BASE_URL}payment/create-order`, {
        proposalId: proposal._id,
        emiStepId: activeEmiId,
      });
      return res.data.id;
    } catch (err) {
      console.error(err);
      const errorMsg =
        err.response?.data?.message ||
        "Could not initiate payment. Please try again.";

      setPaymentStatus({
        emiId: activeEmiId,
        type: "error",
        message: errorMsg,
      });

      toast({
        title: "Initialization Error",
        description: errorMsg,
        status: "error",
        duration: 3000,
      });
    }
  };

  const onApprove = async (data, actions) => {
    try {
      const res = await axios.post(`${BASE_URL}payment/capture-order`, {
        proposalId: proposal._id,
        emiStepId: activeEmiId,
        orderID: data.orderID,
      });

      if (res.status === 200) {
        setPaymentStatus({
          emiId: activeEmiId,
          type: "success",
          message: "Payment Verified! Updating records...",
        });

        toast({
          title: "Payment Successful",
          description: "Your payment has been recorded. Refreshing...",
          status: "success",
          duration: 2000,
        });

        setTimeout(() => window.location.reload(), 2500);
      }
    } catch (err) {
      console.error(err);
      let errorMsg = "Payment failed during capture.";

      if (!err.response) {
        errorMsg =
          "Critical: Payment processed by PayPal but server did not respond. Please contact support with Order ID: " +
          data.orderID;
      } else {
        errorMsg = err.response?.data?.message || "Payment Capture Failed.";
      }

      setPaymentStatus({
        emiId: activeEmiId,
        type: "error",
        message: errorMsg,
      });

      toast({
        title: "Capture Error",
        description: "Please check the error message below.",
        status: "error",
        duration: 5000,
      });
    }
  };

  // --- RAZORPAY HANDLER ---
  const handleRazorpayPayment = async (amount) => {
    clearError();

    // 1. Load the script first!
    const isLoaded = await loadRazorpayScript(
      "https://checkout.razorpay.com/v1/checkout.js"
    );

    if (!isLoaded) {
      setPaymentStatus({
        emiId: activeEmiId,
        type: "error",
        message: "Razorpay SDK failed to load. Check your connection.",
      });
      return;
    }

    try {
      // 2. Create Order on Backend
      const res = await axios.post(`${BASE_URL}payment/razorpay/create-order`, {
        proposalId: proposal._id,
        emiStepId: activeEmiId,
      });

      const orderData = res.data;
      const rzKey = orderData.keyId;
      if (!rzKey) {
        setPaymentStatus({
          emiId: activeEmiId,
          type: "error",
          message: "Razorpay is not configured for this company.",
        });
        return;
      }

      // 3. Open Razorpay Modal
      const options = {
        key: rzKey,
        amount: orderData.amount,
        currency: orderData.currency,
        name: proposal.companyInfo?.companyName || "Payment",
        description: "Payment for " + proposal.proposalName,
        image: proposal.companyInfo?.ComapanyImageOne, // Logo
        order_id: orderData.id,
        handler: async function (response) {
          // 4. Verify on Success
          try {
            const verifyRes = await axios.post(
              `${BASE_URL}payment/razorpay/verify`,
              {
                proposalId: proposal._id,
                emiStepId: activeEmiId,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              }
            );

            if (verifyRes.data.status === "captured") {
              setPaymentStatus({
                emiId: activeEmiId,
                type: "success",
                message: "Payment Successful!",
              });
              setTimeout(() => window.location.reload(), 2000);
            }
          } catch (err) {
            setPaymentStatus({
              emiId: activeEmiId,
              type: "error",
              message: "Verification Failed. Contact Support.",
            });
          }
        },
        prefill: {
          name: proposal.name,
          email: proposal.email,
          contact: proposal.phone,
        },
        theme: {
          color: "#3182ce", // Matches Chakra Blue
        },
        modal: {
          ondismiss: function () {
            setPaymentStatus({
              emiId: activeEmiId,
              type: "warning",
              message: "Payment Cancelled.",
            });
          },
        },
      };

      const rzp1 = new window.Razorpay(options);
      rzp1.open();

      rzp1.on("payment.failed", function (response) {
        setPaymentStatus({
          emiId: activeEmiId,
          type: "error",
          message: response.error.description || "Payment Failed",
        });
      });
    } catch (err) {
      console.error(err);
      setPaymentStatus({
        emiId: activeEmiId,
        type: "error",
        message: "Failed to initiate Razorpay transaction.",
      });
    }
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" h="100vh" bg="gray.50">
        <VStack spacing={4}>
          <Box className="loader" />
          <Text color="gray.500" fontSize="lg">
            Securing Connection...
          </Text>
        </VStack>
      </Flex>
    );
  }

  if (!proposal)
    return (
      <Box p={10} textAlign="center">
        Proposal Not Found
      </Box>
    );

  // --- Calculations ---
  const totalAmount = proposal.total || 0;
  const paymentSummary = proposal.emiSteps?.reduce(
    (acc, curr) => {
      if (curr.isPaid) acc.paid += curr.pay;
      else acc.pending += curr.pay;
      return acc;
    },
    { paid: 0, pending: 0 }
  );

  const progressPercent = (paymentSummary.paid / totalAmount) * 100;
  const isAutoPaypalMode = proposal.paymentMode === "AUTO_PAYPAL";

  // --- SEQUENTIAL LOGIC: Find the first index that is NOT paid ---
  const firstUnpaidIndex = proposal.emiSteps.findIndex((step) => !step.isPaid);

  return (
    <Box minH="100vh" bg="gray.50" pb={10}>
      {/* --- Top Security Bar --- */}
      <Box
        bg="green.600"
        color="white"
        py={2}
        textAlign="center"
        fontSize="sm"
        fontWeight="bold"
      >
        <HStack justify="center" spacing={2}>
          <Icon as={FaLock} />
          <Text>256-Bit SSL Secured Payment Portal</Text>
        </HStack>
      </Box>

      <Container maxW="container.2xl" pt={2}>
        {/* --- Header Section --- */}
        <VStack spacing={2} mb={2} align="stretch">
          <Flex
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            align="center"
            gap={4}
          >
            <HStack spacing={4}>
              {proposal.companyInfo?.ComapanyImageOne && (
                <Image
                  src={proposal.companyInfo.ComapanyImageOne}
                  h="60px"
                  objectFit="contain"
                  alt="Logo"
                />
              )}
              <Box>
                <Heading size="lg" color="gray.700">
                  {proposal.proposalName}
                </Heading>
                <Text color="gray.500" fontSize="sm">
                  Reference ID: {proposal._id}
                </Text>
              </Box>
            </HStack>
            <Badge
              colorScheme={progressPercent === 100 ? "green" : "orange"}
              p={2}
              borderRadius="md"
              fontSize="md"
            >
              {progressPercent === 100 ? "Fully Paid" : "Payment In Progress"}
            </Badge>
          </Flex>

          <Card variant="outline" boxShadow="sm" borderRadius="xl" bg="white">
            <CardBody>
              <Grid
                templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
                gap={6}
                mb={4}
              >
                <Stat>
                  <StatLabel color="gray.500">Total Contract Value</StatLabel>
                  <StatNumber color="blue.600">
                    {proposal.currency} {totalAmount.toLocaleString()}
                  </StatNumber>
                  <StatHelpText>Inclusive of Taxes</StatHelpText>
                </Stat>
                <Stat>
                  <StatLabel color="gray.500">Amount Paid</StatLabel>
                  <StatNumber color="green.500">
                    {proposal.currency} {paymentSummary.paid.toLocaleString()}
                  </StatNumber>
                  <StatHelpText>Received to date</StatHelpText>
                </Stat>
                <Stat>
                  <StatLabel color="gray.500">Pending Balance</StatLabel>
                  <StatNumber color="red.500">
                    {proposal.currency}{" "}
                    {paymentSummary.pending.toLocaleString()}
                  </StatNumber>
                  <StatHelpText>Remaining due</StatHelpText>
                </Stat>
              </Grid>
              <Box>
                <Flex
                  justify="space-between"
                  mb={1}
                  fontSize="xs"
                  fontWeight="bold"
                >
                  <Text color="green.600">
                    {progressPercent.toFixed(0)}% Completed
                  </Text>
                </Flex>
                <Progress
                  value={progressPercent}
                  colorScheme="green"
                  hasStripe
                  isAnimated={progressPercent < 100}
                  borderRadius="full"
                  h="12px"
                />
              </Box>
            </CardBody>
          </Card>
        </VStack>

        <Grid templateColumns={{ base: "1fr", lg: "350px 1fr" }} gap={4}>
          {/* --- LEFT COLUMN --- */}
          <GridItem>
            <VStack spacing={2} align="stretch">
              <Card variant="elevated" borderRadius="xl">
                <CardBody>
                  <HStack mb={4} color="blue.600">
                    <Icon as={FaUser} />
                    <Heading size="sm">Client Details</Heading>
                  </HStack>
                  <VStack align="start" spacing={3} fontSize="sm">
                    <Box>
                      <Text color="gray.500">Name</Text>
                      <Text fontWeight="600">{proposal.name}</Text>
                    </Box>
                    <Box>
                      <Text color="gray.500">Email</Text>
                      <Text fontWeight="600">{proposal.email}</Text>
                    </Box>
                    <Box>
                      <Text color="gray.500">Phone</Text>
                      <Text fontWeight="600">{proposal.phone}</Text>
                    </Box>
                    <Box>
                      <Text color="gray.500">Address</Text>
                      <Text fontWeight="600">{proposal.location}</Text>
                    </Box>
                  </VStack>
                </CardBody>
              </Card>

              <Card variant="elevated" borderRadius="xl">
                <CardBody>
                  <HStack mb={4} color="purple.600">
                    <Icon as={FaBuilding} />
                    <Heading size="sm">Service Provider</Heading>
                  </HStack>
                  <VStack align="start" spacing={3} fontSize="sm">
                    <Box>
                      <Text color="gray.500">Company</Text>
                      <Text fontWeight="600">
                        {proposal.companyInfo?.companyName}
                      </Text>
                    </Box>
                    <Box>
                      <Text color="gray.500">Support Email</Text>
                      <Text fontWeight="600">
                        {proposal.companyInfo?.Email}
                      </Text>
                    </Box>
                    <Box>
                      <Text color="gray.500">Website</Text>
                      <Text
                        fontWeight="600"
                        color="blue.500"
                        as="a"
                        href={proposal.companyInfo?.webURL}
                        target="_blank"
                      >
                        Visit Website
                      </Text>
                    </Box>
                  </VStack>
                </CardBody>
              </Card>

              <Card variant="elevated" borderRadius="xl">
                <CardBody>
                  <HStack mb={4} color="orange.500">
                    <Icon as={FaReceipt} />
                    <Heading size="sm">Included Items</Heading>
                  </HStack>
                  <VStack align="stretch" divider={<Divider />} spacing={3}>
                    {proposal.items.map((item, idx) => (
                      <Flex key={idx} justify="space-between" fontSize="sm">
                        <Text>
                          {item.productName} (x{item.quantity})
                        </Text>
                        <Text fontWeight="bold">
                          {proposal.currency} {item.total}
                        </Text>
                      </Flex>
                    ))}
                    <Flex
                      justify="space-between"
                      fontSize="sm"
                      fontWeight="bold"
                      pt={2}
                    >
                      <Text>Subtotal</Text>
                      <Text>
                        {proposal.currency} {proposal.subTotal}
                      </Text>
                    </Flex>
                    <Flex
                      justify="space-between"
                      fontSize="sm"
                      fontWeight="bold"
                    >
                      <Text>Tax</Text>
                      <Text>
                        {proposal.currency} {proposal.totalTax}
                      </Text>
                    </Flex>
                  </VStack>
                </CardBody>
              </Card>
            </VStack>
          </GridItem>

          {/* --- RIGHT COLUMN: Payment Accordion --- */}
          <GridItem>
            <Card variant="elevated" borderRadius="xl" overflow="hidden">
              <Box
                bg="gray.100"
                p={4}
                borderBottom="1px solid"
                borderColor="gray.200"
              >
                <Heading size="md" display="flex" alignItems="center" gap={2}>
                  <Icon as={FaMoneyBillWave} color="green.600" /> Payment
                  Schedule
                </Heading>
                <Text fontSize="sm" color="gray.500" mt={1}>
                  Payments must be completed in sequential order.
                </Text>
              </Box>

              <CardBody p={0}>
                {/* Set defaultIndex to the firstUnpaidIndex so it opens automatically. */}
                <Accordion
                  allowToggle
                  defaultIndex={
                    firstUnpaidIndex !== -1 ? [firstUnpaidIndex] : []
                  }
                >
                  {proposal.emiSteps.map((emi, index) => {
                    // Logic: Is this specific EMI locked?
                    // It is locked if:
                    // 1. It is NOT paid AND
                    // 2. Its index is NOT the first unpaid index
                    const isLocked = !emi.isPaid && index !== firstUnpaidIndex;

                    return (
                      <AccordionItem
                        key={emi._id}
                        borderTop="none"
                        borderBottom="1px solid"
                        borderColor="gray.100"
                        isDisabled={isLocked}
                      >
                        {({ isExpanded }) => (
                          <>
                            <h2>
                              <AccordionButton
                                _expanded={{
                                  bg: emi.isPaid ? "green.50" : "blue.50",
                                  color: emi.isPaid ? "green.700" : "blue.700",
                                }}
                                py={4}
                                onClick={() => {
                                  if (activeEmiId !== emi._id) clearError();
                                }}
                                _disabled={{
                                  bg: "gray.50",
                                  cursor: "not-allowed",
                                }}
                              >
                                <Box flex="1" textAlign="left">
                                  <Flex align="center" gap={3}>
                                    {emi.isPaid ? (
                                      <Icon
                                        as={FaCheckCircle}
                                        color="green.500"
                                        boxSize={5}
                                      />
                                    ) : isLocked ? (
                                      <Icon
                                        as={FaLock}
                                        color="gray.400"
                                        boxSize={5}
                                      />
                                    ) : (
                                      <Icon
                                        as={FaInfoCircle}
                                        color="orange.400"
                                        boxSize={5}
                                      />
                                    )}
                                    <VStack align="start" spacing={0}>
                                      <Text
                                        fontWeight="bold"
                                        fontSize="md"
                                        color={
                                          isLocked ? "gray.500" : "inherit"
                                        }
                                      >
                                        {emi.label}
                                      </Text>
                                      <Text fontSize="xs" color="gray.500">
                                        {emi.isPaid
                                          ? "Paid on " +
                                            new Date(
                                              emi.emiPaymentReceivedDate
                                            ).toLocaleDateString()
                                          : "Due: " +
                                            new Date(
                                              emi.emiPaymentDate
                                            ).toLocaleDateString()}
                                      </Text>
                                    </VStack>
                                  </Flex>
                                </Box>

                                <HStack spacing={4} mr={2}>
                                  <Text
                                    fontWeight="bold"
                                    fontSize="md"
                                    color={isLocked ? "gray.500" : "inherit"}
                                  >
                                    {proposal.currency} {emi.pay}
                                  </Text>
                                  <Badge
                                    colorScheme={
                                      emi.isPaid
                                        ? "green"
                                        : isLocked
                                        ? "gray"
                                        : "red"
                                    }
                                    variant="subtle"
                                    px={2}
                                    py={1}
                                    borderRadius="full"
                                  >
                                    {emi.isPaid
                                      ? "PAID"
                                      : isLocked
                                      ? "LOCKED"
                                      : "PAY NOW"}
                                  </Badge>
                                </HStack>
                                <AccordionIcon />
                              </AccordionButton>
                            </h2>

                            <AccordionPanel
                              pb={6}
                              bg={
                                isExpanded
                                  ? emi.isPaid
                                    ? "green.50"
                                    : "white"
                                  : "white"
                              }
                            >
                              {emi.isPaid ? (
                                // --- PAID VIEW ---
                                <Grid
                                  templateColumns={{
                                    base: "1fr",
                                    md: "1fr 1fr 1fr 1fr",
                                  }}
                                  gap={4}
                                  fontSize="sm"
                                >
                                  <Box
                                    bg="white"
                                    p={3}
                                    borderRadius="md"
                                    border="1px solid"
                                    borderColor="green.200"
                                  >
                                    <Text
                                      color="gray.500"
                                      fontSize="xs"
                                      textTransform="uppercase"
                                    >
                                      Transaction ID
                                    </Text>
                                    <Text
                                      fontWeight="bold"
                                      fontFamily="monospace"
                                    >
                                      {emi.transactionData?.transactionId ||
                                        "N/A"}
                                    </Text>
                                  </Box>
                                  <Box
                                    bg="white"
                                    p={3}
                                    borderRadius="md"
                                    border="1px solid"
                                    borderColor="green.200"
                                  >
                                    <Text
                                      color="gray.500"
                                      fontSize="xs"
                                      textTransform="uppercase"
                                    >
                                      Order ID
                                    </Text>
                                    <Text
                                      fontWeight="bold"
                                      fontFamily="monospace"
                                    >
                                      {emi.transactionData?.orderId || "N/A"}
                                    </Text>
                                  </Box>
                                  <Box
                                    bg="white"
                                    p={3}
                                    borderRadius="md"
                                    border="1px solid"
                                    borderColor="green.200"
                                  >
                                    <Text
                                      color="gray.500"
                                      fontSize="xs"
                                      textTransform="uppercase"
                                    >
                                      Source
                                    </Text>
                                    <Text fontWeight="bold">
                                      {emi.transactionData?.paymentSource ||
                                        "Manual"}
                                    </Text>
                                  </Box>
                                  <Box
                                    bg="white"
                                    p={3}
                                    borderRadius="md"
                                    border="1px solid"
                                    borderColor="green.200"
                                  >
                                    <Text
                                      color="gray.500"
                                      fontSize="xs"
                                      textTransform="uppercase"
                                    >
                                      Updated By
                                    </Text>
                                    <Text fontWeight="bold">
                                      {emi.emiPayUpdatedBy?.name || "System"}
                                    </Text>
                                  </Box>
                                </Grid>
                              ) : (
                                // --- UNPAID VIEW ---
                                <VStack spacing={4} align="stretch" mt={2}>
                                  {isLocked ? (
                                    // --- LOCKED STATE ---
                                    <Alert status="warning" borderRadius="md">
                                      <Icon as={FaLock} mr={3} />
                                      <Box>
                                        <AlertTitle fontSize="sm">
                                          Payment Locked
                                        </AlertTitle>
                                        <AlertDescription
                                          fontSize="sm"
                                          display="block"
                                        >
                                          Please complete the previous EMI
                                          payment before proceeding with this
                                          one.
                                        </AlertDescription>
                                      </Box>
                                    </Alert>
                                  ) : (
                                    // --- ACTIVE PAYMENT STATE ---
                                    <>
                                      <Text fontSize="sm" color="gray.600">
                                        This payment is currently pending.
                                        Please complete the transaction to
                                        update your records.
                                      </Text>

                                      <Divider />

                                      {/* Error Alert */}
                                      {paymentStatus.emiId === emi._id &&
                                        paymentStatus.message && (
                                          <Alert
                                            status={paymentStatus.type}
                                            borderRadius="md"
                                          >
                                            <AlertIcon />
                                            <Box>
                                              <AlertTitle>
                                                {paymentStatus.type === "error"
                                                  ? "Transaction Failed"
                                                  : "Success"}
                                              </AlertTitle>
                                              <AlertDescription display="block">
                                                {paymentStatus.message}
                                              </AlertDescription>
                                            </Box>
                                            <CloseButton
                                              position="absolute"
                                              right="8px"
                                              top="8px"
                                              onClick={clearError}
                                            />
                                          </Alert>
                                        )}

                                      {isAutoPaypalMode ? (
                                        <Box>
                                          {activeEmiId === emi._id ? (
                                            <Box
                                              position="relative"
                                              zIndex={1}
                                              width="100%"
                                              p={4}
                                              bg="blue.50"
                                              borderRadius="md"
                                              border="1px dashed"
                                              borderColor="blue.300"
                                            >
                                              <Text
                                                fontSize="sm"
                                                fontWeight="bold"
                                                mb={2}
                                                textAlign="center"
                                              >
                                                Secure Checkout via PayPal
                                              </Text>
                                              <PayPalScriptProvider
                                                options={{
                                                  "client-id":
                                                    proposal?.paymentIntegration
                                                      ?.paypalClientId ||
                                                    PAYPAL_CLIENT_ID,
                                                  currency: "USD",
                                                  intent: "capture",
                                                }}
                                              >
                                                <PayPalButtons
                                                  style={{
                                                    layout: "vertical",
                                                    shape: "rect",
                                                    height: 45,
                                                  }}
                                                  createOrder={createOrder}
                                                  onApprove={onApprove}
                                                  onError={(err) => {
                                                    console.error(
                                                      "PayPal Error:",
                                                      err
                                                    );
                                                    setPaymentStatus({
                                                      emiId: emi._id,
                                                      type: "error",
                                                      message:
                                                        "PayPal encountered an error. Please try again.",
                                                    });
                                                  }}
                                                  onCancel={() => {
                                                    setActiveEmiId(null);
                                                    clearError();
                                                  }}
                                                />
                                              </PayPalScriptProvider>
                                              <Button
                                                mt={2}
                                                size="xs"
                                                variant="ghost"
                                                colorScheme="red"
                                                w="full"
                                                onClick={() => {
                                                  setActiveEmiId(null);
                                                  clearError();
                                                }}
                                              >
                                                Cancel Transaction
                                              </Button>
                                            </Box>
                                          ) : (
                                            <Button
                                              size="lg"
                                              colorScheme="blue"
                                              w="full"
                                              rightIcon={
                                                <Icon as={FaMoneyBillWave} />
                                              }
                                              onClick={() => {
                                                setActiveEmiId(emi._id);
                                                clearError();
                                              }}
                                              _hover={{
                                                transform: "translateY(-2px)",
                                                boxShadow: "lg",
                                              }}
                                              transition="all 0.2s"
                                            >
                                              Pay Now ({proposal.currency}{" "}
                                              {emi.pay})
                                            </Button>
                                          )}
                                        </Box>
                                      ) : proposal.paymentMode ===
                                        "AUTO_RAZORPAY" ? (
                                        <Box>
                                          {activeEmiId === emi._id ? (
                                            <Button
                                              colorScheme="blue"
                                              width="full"
                                              onClick={() =>
                                                handleRazorpayPayment(emi.pay)
                                              }
                                            >
                                              Pay with Razorpay
                                            </Button>
                                          ) : (
                                            <Button
                                              size="lg"
                                              colorScheme="blue"
                                              w="full"
                                              rightIcon={
                                                <Icon as={FaMoneyBillWave} />
                                              }
                                              onClick={() => {
                                                setActiveEmiId(emi._id);
                                                clearError();
                                              }}
                                              _hover={{
                                                transform: "translateY(-2px)",
                                                boxShadow: "lg",
                                              }}
                                              transition="all 0.2s"
                                            >
                                              Pay Now ({proposal.currency}{" "}
                                              {emi.pay})
                                            </Button>
                                          )}
                                        </Box>
                                      ) : (
                                        <Box
                                          bg="orange.50"
                                          p={4}
                                          borderRadius="md"
                                          borderLeft="4px solid"
                                          borderColor="orange.400"
                                        >
                                          <Heading
                                            size="xs"
                                            color="orange.800"
                                            mb={1}
                                          >
                                            Manual Payment Mode
                                          </Heading>
                                          <Text
                                            fontSize="sm"
                                            color="orange.700"
                                          >
                                            This proposal is set to Manual
                                            Payment. Please contact the
                                            administration to process this
                                            payment offline.
                                          </Text>
                                        </Box>
                                      )}
                                    </>
                                  )}
                                </VStack>
                              )}
                            </AccordionPanel>
                          </>
                        )}
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </CardBody>
            </Card>
          </GridItem>
        </Grid>
      </Container>
    </Box>
  );
};

export default ClientPaymentPortal;
